import { apiFetch } from "@/lib/api";
import type { HubSupportTicketMessage } from "@/components/HubSupportTicketMessageThread";

export type DocumentRequestKind = "access" | "new_document";

export type DocumentRequestPriority =
  | "imminent_procurement"
  | "planned_procurement"
  | "need_to_review"
  | "incomplete_records"
  | "other";

export const DOCUMENT_REQUEST_PRIORITIES: DocumentRequestPriority[] = [
  "imminent_procurement",
  "planned_procurement",
  "need_to_review",
  "incomplete_records",
  "other",
];

export type DocumentRequestStatus =
  | "pending"
  | "investigating"
  | "granted"
  | "fulfilled"
  | "denied"
  | "cancelled";

export type DocumentRequestRow = {
  id: string;
  created_at: string;
  updated_at: string;
  kind: DocumentRequestKind;
  status: DocumentRequestStatus;
  sku: string;
  document_record_id: string | null;
  document_kind_code: string | null;
  requested_title: string | null;
  priority: DocumentRequestPriority | null;
  requested_by_date: string | null;
  requester_user_id: string;
  requester_email: string;
  requester_display_name: string | null;
  body: string;
  page_path: string | null;
  pending_feedback: boolean;
  resolution_note: string | null;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  assigned_to_user_id: string | null;
  assigned_to_display_name: string | null;
  document_title: string | null;
  document_kind_label: string | null;
  resolved_by_display_name: string | null;
};

export type DocumentRequestMessage = {
  id: string;
  created_at: string;
  author_user_id: string;
  author_email: string;
  author_display_name: string;
  body: string;
  is_from_requester: boolean;
};

export const DOCUMENT_REQUEST_QUERY_KEY = ["document-requests"] as const;

export const DOCUMENT_REQUEST_ASSIGNEES_QUERY_KEY = [
  "document-requests",
  "assignees",
] as const;

export interface DocumentRequestAssignee {
  id: string;
  display_name: string;
}

export function documentRequestKindLabel(kind: DocumentRequestKind): string {
  return kind === "access" ? "Access request" : "Missing document";
}

export function documentRequestPriorityLabel(priority: DocumentRequestPriority): string {
  switch (priority) {
    case "imminent_procurement":
      return "Imminent procurement/production";
    case "planned_procurement":
      return "Planned procurement/production";
    case "need_to_review":
      return "Need to review";
    case "incomplete_records":
      return "Incomplete records";
    case "other":
      return "Other";
    default:
      return priority;
  }
}

export function documentRequestStatusLabel(status: DocumentRequestStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "investigating":
      return "Investigating";
    case "granted":
      return "Access granted";
    case "fulfilled":
      return "Fulfilled";
    case "denied":
      return "Denied";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function documentRequestSummary(row: DocumentRequestRow): string {
  if (row.kind === "access") {
    return row.document_title ?? `Document on ${row.sku}`;
  }
  const title = row.requested_title?.trim();
  if (title) {
    return title;
  }
  if (row.document_kind_label) {
    return `${row.document_kind_label} for ${row.sku}`;
  }
  return `Missing document for ${row.sku}`;
}

export function documentRequestMessagesAsHubThread(
  messages: DocumentRequestMessage[],
): HubSupportTicketMessage[] {
  return messages.map((message) => ({
    id: message.id,
    created_at: message.created_at,
    author_user_id: message.author_user_id,
    author_email: message.author_email,
    author_display_name: message.author_display_name,
    body: message.body,
    is_from_reporter: message.is_from_requester,
  }));
}

export async function fetchMyDocumentRequests(): Promise<{ rows: DocumentRequestRow[] }> {
  return apiFetch<{ rows: DocumentRequestRow[] }>("/api/document-requests/mine");
}

export async function fetchDocumentRequest(id: string): Promise<{
  request: DocumentRequestRow;
  messages: DocumentRequestMessage[];
}> {
  return apiFetch<{ request: DocumentRequestRow; messages: DocumentRequestMessage[] }>(
    `/api/document-requests/${encodeURIComponent(id)}`,
  );
}

export async function fetchDocumentRequestAssignees(): Promise<{
  users: DocumentRequestAssignee[];
}> {
  return apiFetch<{ users: DocumentRequestAssignee[] }>("/api/document-requests/assignees");
}

export async function fetchDocumentRequests(input?: {
  status?: string;
  pendingFeedback?: boolean;
  limit?: number;
}): Promise<{ rows: DocumentRequestRow[] }> {
  const query: Record<string, string> = {};
  if (input?.status) {
    query.status = input.status;
  }
  if (input?.pendingFeedback) {
    query.pendingFeedback = "true";
  }
  if (input?.limit != null) {
    query.limit = String(input.limit);
  }
  return apiFetch<{ rows: DocumentRequestRow[] }>("/api/document-requests", { query });
}

export async function createDocumentRequest(body: {
  kind: DocumentRequestKind;
  sku: string;
  documentRecordId?: string;
  documentKindCode?: string | null;
  requestedTitle?: string | null;
  priority?: DocumentRequestPriority | null;
  requestedByDate?: string | null;
  body: string;
  pagePath?: string | null;
}): Promise<{ ok: boolean; id: string; request: DocumentRequestRow }> {
  return apiFetch<{ ok: boolean; id: string; request: DocumentRequestRow }>("/api/document-requests", {
    method: "POST",
    body,
  });
}

export async function postDocumentRequestMessage(
  id: string,
  messageBody: string,
): Promise<{ ok: boolean; messages: DocumentRequestMessage[] }> {
  return apiFetch<{ ok: boolean; messages: DocumentRequestMessage[] }>(
    `/api/document-requests/${encodeURIComponent(id)}/messages`,
    { method: "POST", body: { body: messageBody } },
  );
}

export async function patchDocumentRequest(
  id: string,
  body: {
    status?: DocumentRequestStatus;
    pendingFeedback?: boolean;
    resolutionNote?: string | null;
    assignedToUserId?: string | null;
  },
): Promise<{ request: DocumentRequestRow }> {
  return apiFetch<{ request: DocumentRequestRow }>(`/api/document-requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body,
  });
}
